import { FC, useCallback } from 'react';
import styled, { css } from 'styled-components';
import NumberSpinButton, { INumberSpinButtonProps } from './NumberSpinButton';
import { DAY, HOUR, MINUTE, breakDownSeconds } from '../../utils/timeUtils';
import ThresholdField from './ThresholdField';

const Container = styled.div`
  display: flex;
  align-items: baseline;
`;

const Metric = styled.span<{ disabled: boolean; hideMarginRight?: boolean }>`
  color: ${p => p.disabled && p.theme.palette.neutralSecondary};
  font-size: 1.1em;
  font-weight: 500;
  margin-left: 0.25rem;
  ${p => !p.hideMarginRight && css`margin-right: 0.75rem;`}
`;

const TimeSpinButton: FC<Omit<INumberSpinButtonProps, 'width'>> = (props) => (
  <NumberSpinButton
    {...props}
    width={60}
  />
);

interface IProps {
  label: string;
  tooltip?: string;
  value: number | null;
  onChange(value: number | null): void;
  minSeconds?: number;
}

const BreakdownTimeEditor: FC<IProps> = ({ label, tooltip, value, onChange, minSeconds }) => {
  const { days, hours, minutes, seconds } = breakDownSeconds(value ?? 0);

  const disabled = value === null;

  const updateValue = useCallback(
    (day: number, hour: number, minute: number, second: number) => onChange(day * DAY + hour * HOUR + minute * MINUTE + second),
    [onChange],
  );

  return (
    <ThresholdField
      label={label}
      tooltip={tooltip}
      enabled={!disabled}
      onEnabledChange={(enabled: boolean) => onChange(enabled ? 1 : null)}
    >
      <Container>
        <TimeSpinButton disabled={disabled}
          value={days}
          onChange={(val) => updateValue(val, hours, minutes, seconds)}
          min={0} max={30}
        />
        <Metric disabled={disabled}>d</Metric>
        <TimeSpinButton disabled={disabled}
          value={hours}
          onChange={(val) => updateValue(days, val, minutes, seconds)}
          min={0} max={23}
          showLeadingZero
        />
        <Metric disabled={disabled}>h</Metric>
        <TimeSpinButton disabled={disabled}
          value={minutes}
          onChange={(val) => updateValue(days, hours, val, seconds)}
          min={0} max={59}
          showLeadingZero
        />
        <Metric disabled={disabled}>m</Metric>
        <TimeSpinButton disabled={disabled}
          value={seconds}
          onChange={(val) => updateValue(days, hours, minutes, val)}
          min={(minSeconds && value && value - minSeconds <= 0) ? minSeconds : 0} max={59}
          showLeadingZero
        />
        <Metric disabled={disabled} hideMarginRight>s</Metric>
      </Container>
    </ThresholdField>
  );
};

export default BreakdownTimeEditor;